import React, { createContext, useContext, useCallback, useRef, useState } from "react";
import {
  TypeHeroToast,
  ToastType,
  ShowToastOptions,
  RATE_LIMIT_MS,
  ANIMATION,
} from "./types";
import { aggregateQueue, mergeIntoStack, getAutoDismissDuration } from "./engine";

let idCounter = 0;
const genId = () => `th-${++idCounter}-${Date.now()}`;


interface ToastAPI {
  toasts: TypeHeroToast[];
  show: (opts: ShowToastOptions) => string;
  dismiss: (id: string) => void;
  updateProgress: (id: string, progress: number) => void;
  morph: (id: string, type: ToastType, title: string, body?: string) => void;
  clearAll: () => void;
  pauseToast: (id: string) => void;
  resumeToast: (id: string) => void;
}

declare global {
  var __TH_TOAST_API__: ToastAPI | null | undefined;
}

const ToastContext = createContext<ToastAPI | null>(null);

export const useToast = (): ToastAPI => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within <TypeHeroToastProvider>");
  return ctx;
};

export const useTypeHeroToast = useToast;

let providerCounter = 0;
let mountedProviders: { id: number; api: ToastAPI }[] = [];

function registerToastAPI(id: number, api: ToastAPI) {
  const existing = mountedProviders.find((entry) => entry.id === id);
  if (existing) {
    existing.api = api;
  } else {
    mountedProviders.push({ id, api });
  }
  globalThis.__TH_TOAST_API__ = getToastAPI();
}

function unregisterToastAPI(id: number) {
  mountedProviders = mountedProviders.filter((entry) => entry.id !== id);
  const remaining = getToastAPI();
  if (remaining) {
    globalThis.__TH_TOAST_API__ = remaining;
  } else {
    delete globalThis.__TH_TOAST_API__;
  }
}

export function getToastAPI(): ToastAPI | null {
  if (mountedProviders.length > 0) {
    return mountedProviders[mountedProviders.length - 1].api;
  }
  return globalThis.__TH_TOAST_API__ || null;
}

export const TypeHeroToastProvider = ({ children }: { children: React.ReactNode }) => {
  const [toasts, setToasts] = useState<TypeHeroToast[]>([]);
  const queueRef = useRef<TypeHeroToast[]>([]);
  const lastEmitRef = useRef(0);
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const dismissTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const exitTimersRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  const clearTimer = useCallback((id: string) => {
    const timer = dismissTimersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      dismissTimersRef.current.delete(id);
    }
  }, []);

  const animateOut = useCallback((id: string) => {
    setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, _exiting: true } : t)));
    const timer = setTimeout(() => {
      exitTimersRef.current.delete(timer);
      setToasts((prev) => prev.filter((t) => t.id !== id));
      dismissTimersRef.current.delete(id);
    }, ANIMATION.exit);
    exitTimersRef.current.add(timer);
  }, []);

  const scheduleAutoDismiss = useCallback(
    (toast: TypeHeroToast) => {
      const dur = getAutoDismissDuration(toast);
      if (dur <= 0) return;
      const timer = setTimeout(() => animateOut(toast.id), dur);
      dismissTimersRef.current.set(toast.id, timer);
    },
    [animateOut]
  );

  const processQueue = useCallback(() => {
    const now = Date.now();

    if (now - lastEmitRef.current < RATE_LIMIT_MS && queueRef.current.length > 0) {
      if (!flushTimerRef.current) {
        flushTimerRef.current = setTimeout(() => {
          flushTimerRef.current = undefined;
          processQueue();
        }, RATE_LIMIT_MS);
      }
      return;
    }

    if (queueRef.current.length === 0) return;

    const aggregated = aggregateQueue(queueRef.current);
    queueRef.current = [];
    lastEmitRef.current = now;

    setToasts((prev) => {
      const { updated, displacedIds, resetTimerIds, newToasts } = mergeIntoStack(
        prev,
        aggregated,
        now
      );

      for (const id of displacedIds) clearTimer(id);

      for (const id of resetTimerIds) {
        clearTimer(id);
        const toast = updated.find((t) => t.id === id);
        if (toast) scheduleAutoDismiss(toast);
      }

      for (const toast of newToasts) scheduleAutoDismiss(toast);

      return updated;
    });

    if (queueRef.current.length > 0) {
      flushTimerRef.current = setTimeout(() => {
        flushTimerRef.current = undefined;
        processQueue();
      }, RATE_LIMIT_MS);
    }
  }, [clearTimer, scheduleAutoDismiss]);

  const show = useCallback(
    (opts: ShowToastOptions): string => {
      const id = genId();
      const toast: TypeHeroToast = {
        id,
        type: opts.type,
        title: opts.title,
        body: opts.body,
        action: opts.action,
        progress: opts.progress,
        _createdAt: Date.now(),
        _dedupeKey: opts.dedupeKey,
        _count: 1,
      };
      queueRef.current.push(toast);
      processQueue();
      return id;
    },
    [processQueue]
  );

  const dismiss = useCallback(
    (id: string) => {
      clearTimer(id);
      animateOut(id);
    },
    [clearTimer, animateOut]
  );

  const updateProgress = useCallback((id: string, progress: number) => {
    setToasts((prev) =>
      prev.map((t) =>
        t.id === id ? { ...t, progress: Math.min(100, Math.max(0, progress)) } : t
      )
    );
  }, []);

  const morph = useCallback(
    (id: string, type: ToastType, title: string, body?: string) => {
      setToasts((prev) =>
        prev.map((t) =>
          t.id === id ? { ...t, type, title, body, progress: undefined } : t
        )
      );
      clearTimer(id);
      const dur = getAutoDismissDuration({ type } as TypeHeroToast);
      if (dur > 0) {
        const timer = setTimeout(() => animateOut(id), dur);
        dismissTimersRef.current.set(id, timer);
      }
    },
    [clearTimer, animateOut]
  );

  const clearAll = useCallback(() => {
    dismissTimersRef.current.forEach((t) => clearTimeout(t));
    dismissTimersRef.current.clear();
    queueRef.current = [];
    setToasts([]);
  }, []);

  const pauseToast = useCallback(
    (id: string) => {
      clearTimer(id);
      setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, _paused: true } : t)));
    },
    [clearTimer]
  );

  const resumeToast = useCallback(
    (id: string) => {
      setToasts((prev) => {
        const toast = prev.find((t) => t.id === id);
        if (toast) {
          const remaining = getAutoDismissDuration(toast) * ANIMATION.resumeFraction;
          if (remaining > 0) {
            setTimeout(() => {
              const timer = setTimeout(() => animateOut(id), remaining);
              dismissTimersRef.current.set(id, timer);
            }, ANIMATION.resumeGrace);
          }
        }
        return prev.map((t) => (t.id === id ? { ...t, _paused: false } : t));
      });
    },
    [animateOut]
  );

  const api: ToastAPI = {
    toasts,
    show,
    dismiss,
    updateProgress,
    morph,
    clearAll,
    pauseToast,
    resumeToast,
  };

  const providerIdRef = useRef<number | undefined>(undefined);
  if (providerIdRef.current === undefined) {
    providerIdRef.current = ++providerCounter;
  }
  const apiRef = useRef(api);
  apiRef.current = api;
  registerToastAPI(providerIdRef.current, api);

  React.useEffect(() => {
    const id = providerIdRef.current as number;
    registerToastAPI(id, apiRef.current);
    return () => unregisterToastAPI(id);
  }, []);

  /**
   * Every toast schedules its own dismissal and exit animation, so a provider
   * that unmounts while any are still on screen would leave those timers to
   * fire setToasts into a tree that no longer exists.
   */
  React.useEffect(() => () => {
    if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
    dismissTimersRef.current.forEach((timer) => clearTimeout(timer));
    dismissTimersRef.current.clear();
    exitTimersRef.current.forEach((timer) => clearTimeout(timer));
    exitTimersRef.current.clear();
  }, []);

  return <ToastContext.Provider value={api}>{children}</ToastContext.Provider>;
};
